import { useState, useMemo, useRef, type MouseEvent } from "react";
import { TrendingUp, ShieldCheck, Sparkles, Flame } from "lucide-react";
import type { AutonomousGoalSpec, MultiDimensionalEvaluationReport, TargetKeywordSpec } from "./types.ts";
import {
  getLieflatNiceScale,
  getLieflatSmoothPath,
  getLieflatSmoothAreaPath,
  LIEFLAT_TOKENS,
} from "./lieflatTokens.ts";

export interface ScaleTrajectoryChartProps {
  goal: AutonomousGoalSpec | null;
  report: MultiDimensionalEvaluationReport | null;
  context?: any | null;
  currencySymbol?: string;
  interventionDate?: string | null;
}

type ChartViewMode = "flywheel" | "tacos";

interface TrajectoryDataPoint {
  day: number;
  label: string;
  dateStr: string;
  totalUnits: number;
  adUnits: number;
  organicUnits: number;
  organicRatioPct: number;
  tacosPct: number;
  adSpend: number;
  keywordRanks: Record<string, number>;
  isCurrentDay: boolean;
  hasObservedData: boolean;
  isFuture: boolean;
  phaseNumber: 1 | 2 | 3 | 4;
  phaseLabel: string;
}

// Aliases for compatibility
const getNiceScale = (rawMax: number, targetCount = 4, isIntegerDiscrete = true) =>
  getLieflatNiceScale(rawMax, targetCount, { isInteger: isIntegerDiscrete });
const getSmoothPath = getLieflatSmoothPath;
const getSmoothAreaPath = getLieflatSmoothAreaPath;

export function ScaleTrajectoryChart({
  goal,
  report,
  context,
  currencySymbol = "€",
  interventionDate,
}: ScaleTrajectoryChartProps) {
  const [viewMode, setViewMode] = useState<ChartViewMode>("flywheel");
  const [rangeDays, setRangeDays] = useState<7 | 14 | 30>(7);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const totalHorizonDays = goal?.schedule_policy?.baseline_horizon_days ?? 21;
  const rawTotalUnits = Number(context?.business_performance?.total_units ?? context?.performance?.total_orders ?? context?.ad_performance?.total_orders) || 0;
  const baselineUnits = Number(goal?.growth_targets?.baseline_daily_units ?? (rawTotalUnits > 0 ? (rawTotalUnits / 30).toFixed(1) : 2.2)) || 2.2;
  const targetUnits = Number(goal?.growth_targets?.target_daily_units ?? 10) || 10;
  const maxTacosPct = Number(goal?.growth_targets?.max_tolerated_tacos ? (goal.growth_targets.max_tolerated_tacos * 100).toFixed(1) : 25.0);
  const rawAdSales = Number(context?.ad_performance?.total_sales ?? context?.performance?.total_sales) || 0;
  const rawAdSpend = Number(context?.ad_performance?.total_spend ?? context?.performance?.total_spend) || 0;
  const rawTotalRevenue = Number(context?.business_performance?.total_revenue ?? (rawAdSales > 0 ? rawAdSales * 1.35 : 0)) || 0;
  const asinRevenue = (rawTotalRevenue > rawAdSales * 3.5 || rawTotalRevenue > 2500)
    ? Math.max(rawAdSales * 1.5, baselineUnits * 30 * (Number(context?.product_metadata?.current_price || 20) || 20))
    : rawTotalRevenue;
  const rawBaselineTacos = asinRevenue > 0 && rawAdSpend > 0
    ? (rawAdSpend / asinRevenue) * 100
    : (context?.business_performance?.overall_tacos ? Number(context.business_performance.overall_tacos) * 100 : (rawAdSales > 0 && rawAdSpend > 0 ? (rawAdSpend / (rawAdSales * 1.35)) * 100 : 24.5));
  const baselineTacosPct = rawBaselineTacos < 8.0 && rawAdSpend > 10 ? 24.5 : Number(rawBaselineTacos.toFixed(1));
  
  const effectiveInterventionDate = interventionDate || goal?.intervention_date;

  // Calculate elapsed days since autopilot launch (strictly anchored to actual intervention_date)
  const elapsedDays = useMemo(() => {
    if (effectiveInterventionDate) {
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
      const rawStart = new Date(effectiveInterventionDate);
      const startStart = new Date(rawStart.getFullYear(), rawStart.getMonth(), rawStart.getDate()).getTime();
      return Math.max(0, Math.round((todayStart - startStart) / (24 * 60 * 60 * 1000)));
    }
    if (typeof report?.days_since_intervention === "number" && report.days_since_intervention >= 0) {
      return report.days_since_intervention;
    }
    return 1;
  }, [effectiveInterventionDate, report?.days_since_intervention]);

  const currentDay = Math.min(totalHorizonDays, elapsedDays);
  // Proportional timeline window (7 / 14 / 30 days)
  const windowDays = Math.max(rangeDays, elapsedDays + 1);

  // Generate calendar dates for the timeline window starting from actual Launch Date (Day 0)
  const windowDates = useMemo(() => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

    let startDate: Date;
    if (effectiveInterventionDate) {
      const raw = new Date(effectiveInterventionDate);
      startDate = new Date(raw.getFullYear(), raw.getMonth(), raw.getDate());
    } else {
      // Fallback: derive start date by subtracting elapsedDays from today
      startDate = new Date(todayStart - elapsedDays * 24 * 60 * 60 * 1000);
    }

    const dates: Array<{ day: number; dateStr: string; label: string; isToday: boolean; isPast: boolean }> = [];
    for (let d = 0; d < windowDays; d++) {
      const targetDate = new Date(startDate.getTime() + d * 24 * 60 * 60 * 1000);
      const mm = String(targetDate.getMonth() + 1).padStart(2, "0");
      const dd = String(targetDate.getDate()).padStart(2, "0");
      const isToday = targetDate.getTime() === todayStart;
      const isPast = targetDate.getTime() <= todayStart;
      const label = d === 0 ? `${mm}/${dd} 启动` : `${mm}/${dd}`;
      const dateStr = `${targetDate.getFullYear()}-${mm}-${dd}`;
      dates.push({ day: d, dateStr, label, isToday, isPast });
    }
    return dates;
  }, [effectiveInterventionDate, elapsedDays, windowDays]);

  // Construct Data Points for actual tracked days only (Day 0 to currentDay)
  const dataPoints: TrajectoryDataPoint[] = useMemo(() => {
    const points: TrajectoryDataPoint[] = [];
    const dailyMetrics = context?.business_performance?.daily_metrics || context?.daily_metrics || [];
    const metricsMap = new Map<string, any>();
    dailyMetrics.forEach((d: any) => {
      if (d.date) metricsMap.set(String(d.date).slice(0, 10), d);
    });

    for (let d = 0; d <= currentDay; d++) {
      const isCurrentDay = d === currentDay;
      const phaseNumber: 1 | 2 | 3 | 4 = d <= 3 ? 1 : d <= 7 ? 2 : d <= 14 ? 3 : 4;
      const phaseLabel = phaseNumber === 1
        ? "阶段 1: 点火启动 (Day 1~3)"
        : phaseNumber === 2
          ? "阶段 2: 排位拉升 (Day 4~7)"
          : phaseNumber === 3
            ? "阶段 3: 飞轮加速 (Day 8~14)"
            : `阶段 4: 边际收网与跃迁 (Day 15~${totalHorizonDays})`;

      const wDate = windowDates[d];
      const dateLabel = wDate ? wDate.label : (d === 0 ? "启动" : `Day ${d}`);
      const dateStr = wDate ? wDate.dateStr : "";
      const match = dateStr ? metricsMap.get(dateStr) : null;

      let totalUnits = 0;
      let adUnits = 0;
      let organicUnits = 0;
      let tacosPct = 0;
      let adSpend = 0;
      let hasObservedData = false;

      if (match) {
        hasObservedData = true;
        // Read exact real values from Amazon SP-API and Ads reports
        const unitPrice = Number(context?.product_metadata?.current_price || 20) || 20;
        adUnits = Number(match.ad_orders ?? match.purchases ?? match.purchases7d ?? 0);
        if (adUnits === 0 && Number(match.ad_sales || 0) > 0) {
          adUnits = Math.max(1, Math.round(Number(match.ad_sales) / unitPrice));
        }
        const rawTotalUnits = Number(match.units_ordered ?? match.total_units ?? adUnits);
        // Single ASIN sanity check: guard against historical unpartitioned multi-country/store-wide total units
        totalUnits = (rawTotalUnits > 18 && adUnits <= 5 && Number(match.ad_sales || 0) < 150)
          ? Math.min(rawTotalUnits, Math.max(adUnits, Math.round(Number(match.ad_sales || 30) / unitPrice * 1.6)))
          : rawTotalUnits;
        if (totalUnits < adUnits) totalUnits = adUnits;
        organicUnits = Math.max(0, totalUnits - adUnits);
        adSpend = Number(match.ad_spend ?? match.spend ?? match.cost ?? 0);
        const orderedProductSales = Number(match.ordered_product_sales ?? match.total_sales ?? match.ad_sales ?? 0);
        tacosPct = orderedProductSales > 0 ? Number(((adSpend / orderedProductSales) * 100).toFixed(1)) : 0;
      } else if (wDate && wDate.isToday) {
        totalUnits = 0;
        adUnits = 0;
        organicUnits = 0;
        adSpend = 0;
        tacosPct = 0;
      } else {
        // Missing daily rows are unknown, not baseline values. Keep them as
        // gaps so the chart never presents fabricated performance.
        totalUnits = 0;
        adUnits = 0;
        organicUnits = 0;
        adSpend = 0;
        tacosPct = 0;
      }

      const organicRatioPct = totalUnits > 0 ? Math.round((organicUnits / totalUnits) * 100) : 0;

      points.push({
        day: d,
        label: dateLabel,
        dateStr,
        totalUnits,
        adUnits,
        organicUnits,
        organicRatioPct,
        tacosPct,
        hasObservedData,
        adSpend,
        keywordRanks: {},
        isCurrentDay,
        isFuture: false,
        phaseNumber,
        phaseLabel
      });
    }

    return points;
  }, [currentDay, totalHorizonDays, baselineUnits, baselineTacosPct, windowDates, context, rawAdSpend]);

  // Chart Dimensions
  const svgWidth = 920;
  const svgHeight = 290;
  const padLeft = 55;
  const padRight = 85;
  const padTop = 32;
  const padBottom = 38;
  const plotWidth = svgWidth - padLeft - padRight;
  const plotHeight = svgHeight - padTop - padBottom;
  const innerPad = 28;

  // Nice Standard Scales for Units & TACOS
  const { ticks: unitTicks, maxAxis: maxUnits } = useMemo(() => {
    const maxVal = Math.max(...dataPoints.map((p) => p.totalUnits), targetUnits);
    return getNiceScale(maxVal, 4, true);
  }, [dataPoints, targetUnits]);

  const { ticks: tacosTicks, maxAxis: maxTacosAxis } = useMemo(() => {
    const maxVal = Math.max(...dataPoints.map((p) => p.tacosPct), maxTacosPct, 25);
    return getNiceScale(maxVal, 4, true);
  }, [dataPoints, maxTacosPct]);

  // Explicit evenly-spaced tick indices to prevent text collisions on 14d/30d horizons
  const displayedTickIndices = useMemo(() => {
    const indices: number[] = [];
    if (windowDays <= 7) {
      for (let i = 0; i < windowDays; i++) indices.push(i);
    } else if (windowDays <= 14) {
      const step = 2;
      for (let i = 0; i < windowDays; i += step) indices.push(i);
      if (windowDays - 1 - indices[indices.length - 1] >= 2) {
        indices.push(windowDays - 1);
      }
    } else {
      const step = 5;
      for (let i = 0; i < windowDays; i += step) indices.push(i);
      if (windowDays - 1 - indices[indices.length - 1] >= 3) {
        indices.push(windowDays - 1);
      }
    }
    return indices;
  }, [windowDays]);

  // Coordinates helper (proportional day step spacing across window)
  const getX = (dayIndex: number) => {
    if (windowDays <= 1) return padLeft + plotWidth / 2;
    const availableWidth = plotWidth - 2 * innerPad;
    return padLeft + innerPad + (dayIndex / (windowDays - 1)) * availableWidth;
  };
  const getYUnits = (val: number) => padTop + plotHeight - (Math.min(maxUnits, Math.max(0, val)) / maxUnits) * plotHeight;
  const getYTacos = (val: number) => padTop + plotHeight - (Math.min(maxTacosAxis, Math.max(0, val)) / maxTacosAxis) * plotHeight;

  // Actual tracked curve point helper (Solid curves only)
  const getActualPoints = (getY: (p: TrajectoryDataPoint) => number) => {
    return dataPoints.filter((p) => p.hasObservedData).map((p) => ({ x: getX(p.day), y: getY(p) }));
  };

  // Mouse move handler
  const handleMouseMove = (e: MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const clientX = e.clientX - rect.left;
    const svgX = (clientX / rect.width) * svgWidth;

    if (svgX < padLeft || svgX > padLeft + plotWidth) {
      setHoverIndex(null);
      return;
    }

    const availableWidth = plotWidth - 2 * innerPad;
    const relativeX = svgX - (padLeft + innerPad);
    const ratio = Math.max(0, Math.min(1, relativeX / availableWidth));
    const day = Math.min(windowDays - 1, Math.max(0, Math.round(ratio * (windowDays - 1))));
    setHoverIndex(day <= currentDay ? day : null);
  };

  // Tooltip ONLY appears on active mouse hover
  const activePoint = hoverIndex !== null && hoverIndex >= 0 && hoverIndex < dataPoints.length ? dataPoints[hoverIndex] : null;

  return (
    <div style={{
      background: "var(--surface)",
      border: "1px solid var(--line)",
      borderRadius: "var(--radius-md)",
      overflow: "hidden",
      boxShadow: "var(--shadow-xs)"
    }}>
      {/* Header with Title, Range Selector & Mode Switcher */}
      <div style={{
        padding: "14px 18px",
        borderBottom: "1px solid var(--line-subtle)",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "12px"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div style={{
            width: "32px",
            height: "32px",
            borderRadius: "8px",
            background: "rgba(13, 148, 136, 0.12)",
            color: "var(--teal)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}>
            <TrendingUp size={17} />
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <strong style={{ fontSize: "14px", color: "var(--ink-heading)" }}>
                {`${totalHorizonDays} 天自动放量生命周期数据跃迁趋势`}
              </strong>
              <span className="scale-rank-badge rising" style={{ fontSize: "11px" }}>
                {`实时追踪 (Day ${currentDay}/${totalHorizonDays})`}
              </span>
            </div>
            <div style={{ fontSize: "11.5px", color: "var(--muted)", marginTop: "2px" }}>
              实测追踪当前 ASIN 真实日销跃迁与自然飞轮转化走势
            </div>
          </div>
        </div>

        {/* View Switchers on Right */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          {/* Time Range Selector */}
          <div style={{
            display: "inline-flex",
            padding: "3px",
            background: "var(--surface-subtle)",
            border: "1px solid var(--line)",
            borderRadius: "var(--radius-sm)",
            gap: "2px"
          }}>
            <button
              type="button"
              onClick={() => setRangeDays(7)}
              style={{
                padding: "5px 10px",
                fontSize: "12px",
                fontWeight: rangeDays === 7 ? 700 : 500,
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                background: rangeDays === 7 ? "var(--surface)" : "transparent",
                color: rangeDays === 7 ? "var(--teal)" : "var(--muted)",
                boxShadow: rangeDays === 7 ? "var(--shadow-xs)" : "none"
              }}
            >
              近 7 天
            </button>
            <button
              type="button"
              onClick={() => setRangeDays(14)}
              style={{
                padding: "5px 10px",
                fontSize: "12px",
                fontWeight: rangeDays === 14 ? 700 : 500,
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                background: rangeDays === 14 ? "var(--surface)" : "transparent",
                color: rangeDays === 14 ? "var(--teal)" : "var(--muted)",
                boxShadow: rangeDays === 14 ? "var(--shadow-xs)" : "none"
              }}
            >
              近 14 天
            </button>
            <button
              type="button"
              onClick={() => setRangeDays(30)}
              style={{
                padding: "5px 10px",
                fontSize: "12px",
                fontWeight: rangeDays === 30 ? 700 : 500,
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                background: rangeDays === 30 ? "var(--surface)" : "transparent",
                color: rangeDays === 30 ? "var(--teal)" : "var(--muted)",
                boxShadow: rangeDays === 30 ? "var(--shadow-xs)" : "none"
              }}
            >
              近 30 天
            </button>
          </div>

          {/* View Mode Buttons */}
          <div style={{
            display: "inline-flex",
            padding: "3px",
            background: "var(--surface-subtle)",
            border: "1px solid var(--line)",
            borderRadius: "var(--radius-sm)",
            gap: "2px"
          }}>
            <button
              type="button"
              onClick={() => setViewMode("flywheel")}
              style={{
                padding: "5px 12px",
                fontSize: "12px",
                fontWeight: viewMode === "flywheel" ? 700 : 500,
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                background: viewMode === "flywheel" ? "var(--surface)" : "transparent",
                color: viewMode === "flywheel" ? "var(--teal)" : "var(--muted)",
                boxShadow: viewMode === "flywheel" ? "var(--shadow-xs)" : "none",
                display: "flex",
                alignItems: "center",
                gap: "6px"
              }}
            >
              <Sparkles size={13} /> 出单飞轮跃迁
            </button>
            <button
              type="button"
              onClick={() => setViewMode("tacos")}
              style={{
                padding: "5px 12px",
                fontSize: "12px",
                fontWeight: viewMode === "tacos" ? 700 : 500,
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                background: viewMode === "tacos" ? "var(--surface)" : "transparent",
                color: viewMode === "tacos" ? "var(--teal)" : "var(--muted)",
                boxShadow: viewMode === "tacos" ? "var(--shadow-xs)" : "none",
                display: "flex",
                alignItems: "center",
                gap: "6px"
              }}
            >
              <ShieldCheck size={13} /> TACOS 控盘与支出
            </button>
          </div>
        </div>
      </div>

      {/* Direct Header Legend for Immediate Glanceability */}
      <div style={{
        padding: "8px 18px 2px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "12px",
        fontSize: "11.5px",
        background: "transparent"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap" }}>
          {viewMode === "flywheel" && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <span style={{ width: "12px", height: "2.5px", background: "#10b981", borderRadius: "2px" }} />
                <span style={{ color: "var(--ink)", fontWeight: 600 }}>实测总日销 (实线)</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <span style={{ width: "10px", height: "2.5px", background: "#0d9488", borderRadius: "2px" }} />
                <span style={{ color: "#0d9488", fontWeight: 600 }}>自然飞轮单 (Organic)</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <span style={{ width: "10px", height: "2.5px", background: "#0284c7", borderRadius: "2px" }} />
                <span style={{ color: "#0284c7", fontWeight: 600 }}>广告撬动单 (Ad)</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <span style={{ width: "12px", height: "2px", borderTop: "2px dashed #f59e0b" }} />
                <span style={{ color: "#d97706", fontWeight: 600 }}>{`冲刺目标线 (${targetUnits}单/天)`}</span>
              </div>
            </>
          )}

          {viewMode === "tacos" && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <span style={{ width: "12px", height: "2.5px", background: "#8b5cf6", borderRadius: "2px" }} />
                <span style={{ color: "var(--ink)", fontWeight: 600 }}>实测 TACOS (实线)</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <span style={{ width: "10px", height: "2px", borderTop: "2px dashed #ef4444" }} />
                <span style={{ color: "#ef4444", fontWeight: 600 }}>{`放量 TACOS 上限警戒 (≤${maxTacosPct}%)`}</span>
              </div>
            </>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span className="scale-rank-badge rising" style={{ fontSize: "11px", padding: "2px 8px" }}>
            <Flame size={11} /> 飞轮动能：预计阶段 3 达成自然单反超
          </span>
        </div>
      </div>

      {/* Interactive SVG Chart Area */}
      <div className="trend-svg-wrapper" style={{ padding: "8px 18px 6px", position: "relative" }}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          className="trend-svg"
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setHoverIndex(null)}
          style={{ width: "100%", height: "auto", cursor: "crosshair" }}
        >
          <defs>
            <linearGradient id="organicAreaGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#0d9488" stopOpacity="0.22" />
              <stop offset="60%" stopColor="#0d9488" stopOpacity="0.06" />
              <stop offset="100%" stopColor="#0d9488" stopOpacity="0.00" />
            </linearGradient>
            <linearGradient id="tacosAreaGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.20" />
              <stop offset="60%" stopColor="#8b5cf6" stopOpacity="0.05" />
              <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.00" />
            </linearGradient>
          </defs>

          {/* 4 SOP Standard Phase Background Regions */}
          <rect x={getX(0)} y={padTop} width={Math.max(0, getX(Math.min(3, windowDays - 1)) - getX(0))} height={plotHeight} fill="rgba(13, 148, 136, 0.03)" />
          {windowDays > 4 && <rect x={getX(3)} y={padTop} width={Math.max(0, getX(Math.min(7, windowDays - 1)) - getX(3))} height={plotHeight} fill="rgba(59, 130, 246, 0.025)" />}
          {windowDays > 8 && <rect x={getX(7)} y={padTop} width={Math.max(0, getX(Math.min(14, windowDays - 1)) - getX(7))} height={plotHeight} fill="rgba(16, 185, 129, 0.025)" />}
          {windowDays > 15 && <rect x={getX(14)} y={padTop} width={Math.max(0, getX(windowDays - 1) - getX(14))} height={plotHeight} fill="rgba(139, 92, 246, 0.025)" />}

          {/* Subtle Phase Vertical Divider Lines */}
          {windowDays > 4 && <line x1={getX(3)} y1={padTop} x2={getX(3)} y2={padTop + plotHeight} stroke="var(--line)" strokeDasharray="3 3" strokeOpacity="0.6" />}
          {windowDays > 8 && <line x1={getX(7)} y1={padTop} x2={getX(7)} y2={padTop + plotHeight} stroke="var(--line)" strokeDasharray="3 3" strokeOpacity="0.6" />}
          {windowDays > 15 && <line x1={getX(14)} y1={padTop} x2={getX(14)} y2={padTop + plotHeight} stroke="var(--line)" strokeDasharray="3 3" strokeOpacity="0.6" />}

          {/* Phase Header Labels */}
          <g>
            <text x={(getX(0) + getX(Math.min(3, windowDays - 1))) / 2} y={padTop - 10} textAnchor="middle" fontSize={windowDays > 14 ? "9.5" : "10"} fill="var(--teal)" fontWeight="600" opacity="0.9">
              {windowDays > 14 ? "P1 点火" : "阶段 1: 点火 (D1~3)"}
            </text>
            {windowDays > 4 && (
              <text x={(getX(3) + getX(Math.min(7, windowDays - 1))) / 2} y={padTop - 10} textAnchor="middle" fontSize={windowDays > 14 ? "9.5" : "10"} fill="#2563eb" fontWeight="600" opacity="0.9">
                {windowDays > 14 ? "P2 拉升" : "阶段 2: 拉升 (D4~7)"}
              </text>
            )}
            {windowDays > 8 && (
              <text x={(getX(7) + getX(Math.min(14, windowDays - 1))) / 2} y={padTop - 10} textAnchor="middle" fontSize={windowDays > 14 ? "9.5" : "10"} fill="#059669" fontWeight="600" opacity="0.9">
                {windowDays > 14 ? "P3 飞轮 (D8~14)" : "阶段 3: 飞轮 (D8~14)"}
              </text>
            )}
            {windowDays > 15 && (
              <text x={(getX(14) + getX(windowDays - 1)) / 2} y={padTop - 10} textAnchor="middle" fontSize={windowDays > 14 ? "9.5" : "10"} fill="#8b5cf6" fontWeight="600" opacity="0.9">
                {windowDays > 14 ? `P4 收网 (D15~${totalHorizonDays})` : `阶段 4: 收网 (D15~${totalHorizonDays})`}
              </text>
            )}
          </g>

          {/* Horizontal Grid lines using Nice Standard Ticks */}
          {(viewMode === "flywheel" ? unitTicks : tacosTicks).map((val) => {
            const y = viewMode === "flywheel" ? getYUnits(val) : getYTacos(val);
            return (
              <line
                key={val}
                x1={padLeft}
                y1={y}
                x2={padLeft + plotWidth}
                y2={y}
                stroke="var(--line)"
                strokeDasharray="2 3"
                strokeOpacity="0.6"
              />
            );
          })}

          {/* MODE 1: Flywheel Units */}
          {viewMode === "flywheel" && (
            <>
              {/* Left Y-Axis labels (Units) using Standard Ticks */}
              {unitTicks.map((val) => {
                const y = getYUnits(val);
                return (
                  <text key={val} x={padLeft - 8} y={y + 3.5} textAnchor="end" fontSize="10" fill="var(--muted)" fontWeight="500">
                    {val}单
                  </text>
                );
              })}

              {/* Target Line & Right Milestone Badge */}
              <line
                x1={padLeft}
                y1={getYUnits(targetUnits)}
                x2={padLeft + plotWidth}
                y2={getYUnits(targetUnits)}
                stroke="#f59e0b"
                strokeWidth="1.2"
                strokeDasharray="4 3"
                strokeOpacity="0.55"
              />
              <text
                x={padLeft + plotWidth + 6}
                y={getYUnits(targetUnits) + 3.5}
                textAnchor="start"
                fontSize="10.5"
                fill="#d97706"
                fontWeight="700"
              >
                🎯 目标 {targetUnits}单
              </text>

              {/* Measured Organic Area */}
              {dataPoints.length > 1 && (
                <path
                  d={getSmoothAreaPath(getActualPoints((p) => getYUnits(p.organicUnits)), padTop + plotHeight)}
                  fill="url(#organicAreaGradient)"
                />
              )}

              {/* Organic Units: Actual Solid */}
              <path
                d={getSmoothPath(getActualPoints((p) => getYUnits(p.organicUnits)))}
                fill="none"
                stroke="#0d9488"
                strokeWidth="2.2"
                strokeLinecap="round"
              />

              {/* Ad Units: Actual Solid */}
              <path
                d={getSmoothPath(getActualPoints((p) => getYUnits(p.adUnits)))}
                fill="none"
                stroke="#0284c7"
                strokeWidth="2.2"
                strokeLinecap="round"
              />

              {/* Total Units: Actual Solid */}
              <path
                d={getSmoothPath(getActualPoints((p) => getYUnits(p.totalUnits)))}
                fill="none"
                stroke="#10b981"
                strokeWidth="2.8"
                strokeLinecap="round"
              />

              {/* Highlight Measured Node with Concentric Halo */}
              {dataPoints[currentDay] && (
                <>
                  <circle
                    cx={getX(currentDay)}
                    cy={getYUnits(dataPoints[currentDay].totalUnits)}
                    r="8"
                    fill="#10b981"
                    opacity="0.2"
                  />
                  <circle
                    cx={getX(currentDay)}
                    cy={getYUnits(dataPoints[currentDay].totalUnits)}
                    r="4"
                    fill="#10b981"
                    stroke="var(--surface)"
                    strokeWidth="2"
                  />
                </>
              )}
            </>
          )}

          {/* MODE 2: TACOS & Spend */}
          {viewMode === "tacos" && (
            <>
              {/* Left Y-Axis labels (TACOS %) using Standard Ticks */}
              {tacosTicks.map((val) => {
                const y = getYTacos(val);
                return (
                  <text key={val} x={padLeft - 8} y={y + 3.5} textAnchor="end" fontSize="10" fill="var(--muted)" fontWeight="500">
                    {val}%
                  </text>
                );
              })}

              {/* Max Tolerated TACOS Cap Line & Right Badge Pill */}
              <line
                x1={padLeft}
                y1={getYTacos(maxTacosPct)}
                x2={padLeft + plotWidth}
                y2={getYTacos(maxTacosPct)}
                stroke="#ef4444"
                strokeWidth="1.2"
                strokeDasharray="4 3"
                strokeOpacity="0.65"
              />
              <text
                x={padLeft + plotWidth + 6}
                y={getYTacos(maxTacosPct) + 3.5}
                textAnchor="start"
                fontSize="10.5"
                fill="#ef4444"
                fontWeight="700"
              >
                上限 ≤{maxTacosPct}%
              </text>

              {/* TACOS Area */}
              {dataPoints.length > 1 && (
                <path
                  d={getSmoothAreaPath(getActualPoints((p) => getYTacos(p.tacosPct)), padTop + plotHeight)}
                  fill="url(#tacosAreaGradient)"
                />
              )}

              {/* TACOS Curves: Actual Solid */}
              <path
                d={getSmoothPath(getActualPoints((p) => getYTacos(p.tacosPct)))}
                fill="none"
                stroke="#8b5cf6"
                strokeWidth="2.6"
                strokeLinecap="round"
              />

              {/* Highlight Measured Node with Halo */}
              {dataPoints[currentDay] && (
                <>
                  <circle
                    cx={getX(currentDay)}
                    cy={getYTacos(dataPoints[currentDay].tacosPct)}
                    r="9"
                    fill="#8b5cf6"
                    opacity="0.2"
                  />
                  <circle
                    cx={getX(currentDay)}
                    cy={getYTacos(dataPoints[currentDay].tacosPct)}
                    r="4.5"
                    fill="#8b5cf6"
                    stroke="var(--surface)"
                    strokeWidth="2"
                  />
                </>
              )}
            </>
          )}

          {/* Current Day Marker Line & Clean Capsule Badge */}
          <line
            x1={getX(currentDay)}
            y1={padTop}
            x2={getX(currentDay)}
            y2={padTop + plotHeight}
            stroke="var(--teal)"
            strokeWidth="1.5"
            strokeDasharray="3 3"
          />
          <rect
            x={getX(currentDay) - 17}
            y={padTop + plotHeight + 4}
            width="34"
            height="17"
            rx="8.5"
            fill="var(--teal)"
            filter="drop-shadow(0 1px 2px rgba(13, 148, 136, 0.3))"
          />
          <text
            x={getX(currentDay)}
            y={padTop + plotHeight + 16}
            textAnchor="middle"
            fontSize="9.5"
            fontWeight="700"
            fill="#ffffff"
          >
            今日
          </text>

          {/* Active Hover Crosshair Guideline */}
          {hoverIndex !== null && (
            <line
              x1={getX(hoverIndex)}
              y1={padTop}
              x2={getX(hoverIndex)}
              y2={padTop + plotHeight}
              stroke="var(--ink-heading)"
              strokeWidth="1.2"
              strokeDasharray="3 3"
              opacity="0.5"
            />
          )}

          {/* X-Axis Date Labels (Proportional timeline window starting from Launch Date) */}
          {displayedTickIndices.map((d) => {
            if (d === currentDay && currentDay > 0) return null; // Rendered with Today highlighted pill
            const p = windowDates[d];
            if (!p) return null;
            const x = getX(d);
            const isFirst = d === 0;
            const isLast = d === windowDays - 1;
            const textAnchor = isFirst && currentDay <= 2 ? "end" : isLast ? "end" : "middle";
            const textX = isFirst && currentDay <= 2 ? (currentDay === 0 ? x - 22 : x - 4) : isLast ? x + 2 : x;

            return (
              <g key={p.dateStr} opacity={p.isPast ? 1 : 0.45}>
                <line x1={x} y1={padTop + plotHeight} x2={x} y2={padTop + plotHeight + 4} stroke="var(--line)" />
                <text
                  x={textX}
                  y={padTop + plotHeight + 16}
                  textAnchor={textAnchor}
                  fontSize="10"
                  fill={d === 0 ? "var(--teal)" : "var(--muted)"}
                  fontWeight={d === 0 ? "700" : "500"}
                >
                  {p.label}
                </text>
              </g>
            );
          })}
        </svg>

        {/* Interactive Floating Tooltip */}
        {activePoint && hoverIndex !== null && (
          <div
            className="trend-tooltip"
            style={{
              left: `${Math.max(12, Math.min(84, (getX(hoverIndex) / svgWidth) * 100))}%`,
              top: "14px",
              background: "var(--surface-raised)",
              border: "1px solid var(--line-strong)",
              borderRadius: "8px",
              padding: "10px 14px",
              boxShadow: "var(--shadow-lg)",
              backdropFilter: "blur(12px)"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid var(--line-subtle)", paddingBottom: "6px", marginBottom: "6px", gap: "12px" }}>
              <strong style={{ color: "var(--ink-heading)", fontSize: "12.5px" }}>
                {activePoint.dateStr || activePoint.label} · {hoverIndex === 0 ? "🚀 启动日" : (hoverIndex === currentDay ? "今日实测" : `运行第 ${hoverIndex} 天`)}
              </strong>
              <span style={{ fontSize: "11px", color: "var(--teal)", fontWeight: 700 }}>
                {activePoint.phaseLabel.split(" ")[1]}
              </span>
            </div>

            {viewMode === "flywheel" ? (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 14px", fontSize: "11.5px" }}>
                <div>
                  <span style={{ color: "var(--muted)" }}>全盘总日销: </span>
                  <strong style={{ color: "#10b981" }}>{activePoint.totalUnits} 单/天</strong>
                </div>
                <div>
                  <span style={{ color: "var(--muted)" }}>自然飞轮单: </span>
                  <strong style={{ color: "#0d9488" }}>{activePoint.organicUnits} 单 ({activePoint.organicRatioPct}%)</strong>
                </div>
                <div>
                  <span style={{ color: "var(--muted)" }}>广告撬动单: </span>
                  <strong style={{ color: "#0284c7" }}>{activePoint.adUnits} 单</strong>
                </div>
                <div>
                  <span style={{ color: "var(--muted)" }}>冲刺达标率: </span>
                  <strong style={{ color: activePoint.totalUnits >= targetUnits ? "#10b981" : "#d97706" }}>
                    {Math.round((activePoint.totalUnits / targetUnits) * 100)}%
                  </strong>
                </div>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 14px", fontSize: "11.5px" }}>
                <div>
                  <span style={{ color: "var(--muted)" }}>全盘 TACOS: </span>
                  <strong style={{ color: activePoint.tacosPct <= maxTacosPct ? "#8b5cf6" : "#ef4444" }}>
                    {activePoint.tacosPct}%
                  </strong>
                </div>
                <div>
                  <span style={{ color: "var(--muted)" }}>放量容忍上限: </span>
                  <strong style={{ color: "var(--muted)" }}>≤{maxTacosPct}%</strong>
                </div>
                <div>
                  <span style={{ color: "var(--muted)" }}>日广告花费: </span>
                  <strong style={{ color: "var(--ink)" }}>{currencySymbol}{activePoint.adSpend}</strong>
                </div>
                <div>
                  <span style={{ color: "var(--muted)" }}>控盘状态: </span>
                  <strong style={{ color: activePoint.tacosPct <= maxTacosPct ? "#10b981" : "#ef4444" }}>
                    {activePoint.tacosPct <= maxTacosPct ? "稳健控盘" : "超标预警"}
                  </strong>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
